import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'aiCampaign/recipientEmail';

/**
 * Fetched only when a recipient is expanded. A campaign can have hundreds of recipients and each body is a full
 * email, so they are never loaded as a set.
 */
export function useAICampaignRecipientEmail({
	bulkEmailMessageId,
	contactId,
	enabled = true,
}: {
	bulkEmailMessageId: string;
	contactId: string;
	enabled?: boolean;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled: enabled && !!bulkEmailMessageId && !!contactId,
		queryFn: async () => {
			return userSession.webServiceHelper.callAsync<Api.ICampaignEmailContent>(
				`reports/Campaign/${encodeURIComponent(bulkEmailMessageId)}/Transactions/${encodeURIComponent(contactId)}`,
				'GET'
			);
		},
		queryKey: [BASE_QUERY_KEY, bulkEmailMessageId, contactId],
		meta: {
			toastErrorMessage: 'Failed to load this email',
		},
	});
}

export const invalidateAICampaignRecipientEmail = (bulkEmailMessageId: string, contactId: string) => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY, bulkEmailMessageId, contactId] });
};

/**
 * Drops a recipient's cached email without triggering a refetch, for a recipient who no longer exists: invalidating
 * would re-request an email the API can only 404 on, surfacing a load-failure toast for a successful removal.
 */
export const dropAICampaignRecipientEmail = (bulkEmailMessageId: string, contactId: string) => {
	reactQueryClient.removeQueries({ queryKey: [BASE_QUERY_KEY, bulkEmailMessageId, contactId] });
};
